import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Input from '@mui/joy/Input';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Stack from '@mui/joy/Stack';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import IconButton from '@mui/joy/IconButton';
import Table from '@mui/joy/Table';
import Switch from '@mui/joy/Switch';
import Alert from '@mui/joy/Alert';
import Link from '@mui/joy/Link';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CircularProgress from '@mui/joy/CircularProgress';
import { attributeSetsApi } from '../../api/attribute-sets.api';
import { attributesApi } from '../../api/attributes.api';
import { Attribute } from '../../types/attribute';
import { AttributeSet, AttributeSetItem } from '../../types/attribute-set';

const AttributeSetForm: React.FC = () => {
  const navigate = useNavigate();
  const { reference } = useParams<{ reference?: string }>();
  const [loading, setLoading] = useState(false);
  const [savingSet, setSavingSet] = useState(false);
  const [addingAttribute, setAddingAttribute] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [currentSet, setCurrentSet] = useState<AttributeSet | null>(null);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [setItems, setSetItems] = useState<AttributeSetItem[]>([]);
  const [selectedAttributeRef, setSelectedAttributeRef] = useState<string | null>(null);
  const [isRequired, setIsRequired] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setError(null);
        setLoading(true);
        const attrs = await attributesApi.getAll();
        setAttributes(attrs);
        if (reference) {
          const set = await attributeSetsApi.getOne(reference);
          setCurrentSet(set);
          setName(set.name);
          setSetItems(set.items || []);
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Error al cargar los datos';
        setError(errorMsg);
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [reference]);

  const handleSaveSet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('El nombre del set es requerido');
      return;
    }
    try {
      setError(null);
      setSavingSet(true);
      if (reference) {
        if (!currentSet) {
          setError('Attribute set no cargado');
          return;
        }
        await attributeSetsApi.update(reference, { name });
      } else {
        // Para nuevos sets, crear primero
        const newSet = await attributeSetsApi.create({ name });
        setCurrentSet(newSet);
        
        // Recargar atributos para asegurar que tenemos la lista actualizada
        try {
          const updatedAttrs = await attributesApi.getAll();
          setAttributes(updatedAttrs);
        } catch (attrErr) {
          console.error('Error reloading attributes:', attrErr);
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error al guardar';
      setError(errorMsg);
      console.error('Error saving attribute set:', err);
    } finally {
      setSavingSet(false);
    }
  };

  const handleAddAttribute = async () => {
    if (!selectedAttributeRef || !currentSet) {
      setError('Debes seleccionar un atributo');
      return;
    }
    try {
      setError(null);
      setAddingAttribute(true);
      const newItem = await attributeSetsApi.addItem(currentSet.reference, {
        attributeReference: selectedAttributeRef,
        sortOrder: setItems.length,
        isRequired,
      } as any);
      setSetItems([...setItems, newItem]);
      setSelectedAttributeRef(null);
      setIsRequired(false);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error al agregar atributo';
      setError(errorMsg);
      console.error('Error adding attribute:', err);
    } finally {
      setAddingAttribute(false);
    }
  };

  const handleRemoveAttribute = async (attributeReference: string) => {
    if (!currentSet) return;
    try {
      setError(null);
      await attributeSetsApi.removeItemByAttributeRef(currentSet.reference, attributeReference);
      setSetItems(setItems.filter((item) => item.attribute?.reference !== attributeReference));
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error al eliminar atributo';
      setError(errorMsg);
      console.error('Error removing attribute:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1000, mx: 'auto', width: '100%' }}>
      <Typography level="h2" sx={{ mb: 1 }}>
        {reference ? 'Editar Conjunto de Atributos' : 'Crear Conjunto de Atributos'}
      </Typography>
      <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 3 }}>
        {reference 
          ? 'Administra los atributos que forman parte de este conjunto'
          : 'Define un nuevo conjunto de atributos que se puede asignar a categorías'
        }
      </Typography>

      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* PASO 1: Nombre del Set */}
      <Sheet variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 'md' }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: currentSet ? 'success.main' : 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
              }}
            >
              1
            </Box>
            <Typography level="title-md">
              {currentSet ? 'Información del Conjunto' : 'Crear Nuevo Conjunto'}
            </Typography>
            {currentSet && (
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography level="body-sm" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                  ✓ Creado
                </Typography>
              </Box>
            )}
          </Box>

          <FormControl required>
            <FormLabel>Nombre del Conjunto</FormLabel>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Atributos de Laptops"
              disabled={currentSet && reference ? true : false}
              slotProps={{
                input: {
                  style: {
                    fontSize: '16px',
                  },
                },
              }}
            />
            <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
              {currentSet && reference 
                ? 'El nombre no se puede cambiar después de crear el conjunto'
                : 'Elige un nombre descriptivo para este conjunto de atributos'
              }
            </Typography>
          </FormControl>

          {!currentSet && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
                pt: 1,
              }}
            >
              <Button 
                variant="outlined" 
                color="neutral" 
                onClick={() => navigate('/attribute-sets')}
              >
                Cancelar
              </Button>
              <Button 
                color="primary" 
                loading={savingSet}
                onClick={async (e: any) => {
                  e.preventDefault();
                  await handleSaveSet(e as any);
                }}
              >
                Crear Conjunto
              </Button>
            </Box>
          )}
        </Stack>
      </Sheet>

      {/* PASO 2: Agregar Atributos */}
      {currentSet && (
        <Sheet variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 'md' }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: setItems.length > 0 ? 'success.main' : 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                }}
              >
                2
              </Box>
              <Typography level="title-md">
                Agregar Atributos
              </Typography>
              {setItems.length > 0 && (
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography level="body-sm" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    {setItems.length} {setItems.length === 1 ? 'atributo agregado' : 'atributos agregados'}
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
              Selecciona los atributos que formarán parte de este conjunto. Los atributos que agregues aquí estarán disponibles en los productos que usen esta categoría.
            </Typography>

            {attributes.length === 0 ? (
              <Box sx={{ py: 2, px: 2, bgcolor: 'warning.softBg', borderRadius: 'sm' }}>
                <Typography level="body-sm" sx={{ color: 'warning.main' }}>
                  📌 Primero necesitas crear atributos.{' '}
                  <Link 
                    component="button" 
                    onClick={() => navigate('/attributes/new')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Ir a crear atributos
                  </Link>
                </Typography>
              </Box>
            ) : (() => {
              const availableAttributes = attributes.filter(
                (attr) => !setItems.some((item) => item.attributeId === attr.id)
              );
              
              return (
                <>
                  {availableAttributes.length === 0 ? (
                    <Box sx={{ py: 2, px: 2, bgcolor: 'success.softBg', borderRadius: 'sm' }}>
                      <Typography level="body-sm" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        ✓ Todos los atributos creados están en este conjunto
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      <FormControl>
                        <FormLabel>Atributo a agregar</FormLabel>
                        <Select
                          value={selectedAttributeRef || ''}
                          onChange={(_, val) => setSelectedAttributeRef(val as string | null)}
                          placeholder="Elige un atributo disponible"
                        >
                          {availableAttributes.map((attr) => (
                            <Option key={attr.reference} value={attr.reference}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                <span>{attr.name}</span>
                                <span style={{ opacity: 0.6 }}>({attr.dataType})</span>
                              </Box>
                            </Option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                        <Box>
                          <FormLabel>Requerido en el formulario</FormLabel>
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                            Los campos requeridos deben ser completados
                          </Typography>
                        </Box>
                        <Switch 
                          checked={isRequired} 
                          onChange={(e) => setIsRequired(e.target.checked)} 
                        />
                      </FormControl>

                      <Button
                        startDecorator={<AddRoundedIcon />}
                        color="primary"
                        onClick={handleAddAttribute}
                        disabled={!selectedAttributeRef || addingAttribute}
                        loading={addingAttribute}
                        size="lg"
                      >
                        Agregar Atributo
                      </Button>
                    </Stack>
                  )}
                </>
              );
            })()}
          </Stack>
        </Sheet>
      )}

      {/* PASO 3: Resumen de Atributos */}
      {currentSet && (
        <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'md' }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: setItems.length > 0 ? 'success.main' : 'neutral.300',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                }}
              >
                3
              </Box>
              <Typography level="title-md">
                Resumen del Conjunto
              </Typography>
            </Box>

            {setItems.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  No hay atributos en este conjunto aún.<br />
                  Agrega al menos uno para poder usar este conjunto en categorías.
                </Typography>
              </Box>
            ) : (
              <Sheet variant="soft" sx={{ overflow: 'auto', borderRadius: 'sm' }}>
                <Table size="sm" hoverRow>
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Atributo</th>
                      <th style={{ width: '20%' }}>Tipo</th>
                      <th style={{ width: '15%' }}>Requerido</th>
                      <th style={{ width: '15%' }}>Para Variantes</th>
                      <th style={{ width: '10%' }} align="center">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {setItems
                      .map((item, idx) => {
                        const attr = item.attribute
                          || attributes.find((a) => a.reference === item.attribute?.reference);
                        return (
                          <tr key={item.id ?? idx}>
                            <td>
                              <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                                {attr?.name || 'Desconocido'}
                              </Typography>
                            </td>
                            <td>
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 'sm',
                                  bgcolor: 'neutral.100',
                                  fontSize: '12px',
                                }}
                              >
                                {attr?.dataType || '-'}
                              </Box>
                            </td>
                            <td>
                              <Typography level="body-sm">
                                {item.isRequired ? '✓ Sí' : '○ No'}
                              </Typography>
                            </td>
                            <td>
                              <Typography level="body-sm">
                                {attr?.isVariantAxis ? '✓ Sí' : '○ No'}
                              </Typography>
                            </td>
                            <td align="center">
                              <IconButton
                                size="sm"
                                variant="plain"
                                color="danger"
                                onClick={() => attr?.reference && handleRemoveAttribute(attr.reference)}
                              >
                                <DeleteRoundedIcon />
                              </IconButton>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>
              </Sheet>
            )}

            {currentSet && reference && (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  color="neutral" 
                  onClick={() => navigate('/attribute-sets')}
                >
                  Volver
                </Button>
                <Button 
                  color="primary" 
                  loading={savingSet}
                  onClick={async (e: any) => {
                    e.preventDefault();
                    await handleSaveSet(e as any);
                  }}
                >
                  Guardar Cambios
                </Button>
              </Box>
            )}
          </Stack>
        </Sheet>
      )}
    </Box>
  );
};

export default AttributeSetForm;
